import type {
  IAttendanceDashboardRejectedShiftChangeDetail,
  IAttendanceDashboardUnavailableUserDetail,
} from '@src/models/interfaces/attendanceDashboard.interface';
import type {
  IWorkJourneyManagementAttentionItem,
  IWorkJourneyManagementUser,
} from '@src/models/interfaces/workJourneyManagement.interface';
import { DateUtil } from '@src/packages/utils/dates';
import {
  getRouteToCoverAbsenceDrawer,
  getRouteToCoverShortageDrawer,
  getRouteToResolveRejectedChangeDrawer,
} from '@src/packages/workJourney/constants';
import { ATTENDANCE_DASHBOARD_STATES } from '@src/packages/workJourney/screens/WorkJourneyManagement/constants';

import { formatCoverUserDisplayName } from './formatCoverUserDisplayName';
import type { IWorkJourneyCoverDrawerLocationState, IWorkJourneyRejectedDrawerUser } from './types';

const {
  UNDERSTAFFED_DUE_TO_ABSENCES,
  USERS_UNAVAILABLE,
  REJECTED_SHIFT_CHANGE,
  INSUFFICIENT_ASSIGNED_MEMBERS,
} = ATTENDANCE_DASHBOARD_STATES;

const mapUnavailableUserToDrawerUser = (
  user: IAttendanceDashboardUnavailableUserDetail,
): IWorkJourneyManagementUser => {
  const firstName = user.firstName ?? '';
  const lastName = user.lastName ?? '';
  return {
    id: user.employeeId,
    displayName: formatCoverUserDisplayName({
      id: user.employeeId,
      displayName: user.displayName,
      firstName,
      lastName,
    }),
    email: '',
    firstName,
    lastName,
    idCard: user.idCard ?? '',
    ...(user.attendanceRecordId ? { attendanceRecordId: user.attendanceRecordId } : {}),
    ...(user.exceptionId ? { exceptionId: user.exceptionId } : {}),
  };
};

const mapRejectedUserToDrawerUser = (
  user: IAttendanceDashboardRejectedShiftChangeDetail,
): IWorkJourneyRejectedDrawerUser => {
  const firstName = user.firstName ?? '';
  const lastName = user.lastName ?? '';
  return {
    id: user.employeeId,
    displayName: formatCoverUserDisplayName({
      id: user.employeeId,
      displayName: user.displayName,
      firstName,
      lastName,
    }),
    email: '',
    firstName,
    lastName,
    idCard: user.idCard ?? '',
    eventId: user.eventId,
    attendanceRecordId: user.attendanceRecordId,
    attendanceType: user.attendanceType,
  };
};

const resolveCurrentMembers = (item: IWorkJourneyManagementAttentionItem): number => {
  const { details, assignedMembers, state } = item;

  if (state === UNDERSTAFFED_DUE_TO_ABSENCES || state === USERS_UNAVAILABLE) {
    if (details?.presentWorkers != null) return details.presentWorkers;
  }

  return details?.assignedMembers ?? assignedMembers;
};

const resolveRequiredMembers = (item: IWorkJourneyManagementAttentionItem): number =>
  item.details?.requiredMembers ?? item.requiredMembers;

const resolveAbsentUsers = (item: IWorkJourneyManagementAttentionItem): IWorkJourneyManagementUser[] =>
  (item.details?.absentUsers ?? item.details?.unavailableUsers ?? []).map(
    mapUnavailableUserToDrawerUser,
  );

const resolveRejectedUsers = (
  item: IWorkJourneyManagementAttentionItem,
): IWorkJourneyRejectedDrawerUser[] =>
  (item.details?.rejectedShiftChanges ?? []).map(mapRejectedUserToDrawerUser);

const resolveDrawerDate = (item: IWorkJourneyManagementAttentionItem): string => {
  const today = DateUtil.toDateString(new Date(), 'YYYY-MM-DD');
  const from = item.dateFrom ?? item.date;
  const to = item.dateTo ?? from;

  if (from && to && today && from <= today && today <= to) return today;
  if (from) {
    const normalized = DateUtil.toDateString(from, 'YYYY-MM-DD');
    if (normalized && normalized !== '-') return normalized;
  }
  if (today) return today;
  return DateUtil.toDateString(new Date(), 'YYYY-MM-DD');
};

const isRejectedChangeItem = (item: IWorkJourneyManagementAttentionItem): boolean =>
  item.state === REJECTED_SHIFT_CHANGE ||
  item.actionType === 'resolve' ||
  item.reason === 'rejected_change';

/**
 * Builds the relative path and location state to open the matching cover / resolve drawer.
 */
export const getCoverPositionNavigation = (
  item: IWorkJourneyManagementAttentionItem,
): { path: string; state: IWorkJourneyCoverDrawerLocationState } => {
  const workJourneyConfigId = item.workJourneyConfigId;
  const date = resolveDrawerDate(item);
  const dateFrom = item.dateFrom ?? item.date;
  const dateTo = item.dateTo ?? dateFrom;

  const baseState: IWorkJourneyCoverDrawerLocationState = {
    shadow: true,
    journeyName: item.workJourneyConfigName,
    currentMembers: resolveCurrentMembers(item),
    requiredMembers: resolveRequiredMembers(item),
  };

  if (isRejectedChangeItem(item)) {
    return {
      path: getRouteToResolveRejectedChangeDrawer({ workJourneyConfigId, date }),
      state: {
        ...baseState,
        rejectedUsers: resolveRejectedUsers(item),
        planning: {
          effectiveFrom: date,
          journeyName: item.workJourneyConfigName,
        },
      },
    };
  }

  if (item.state === UNDERSTAFFED_DUE_TO_ABSENCES || item.state === USERS_UNAVAILABLE) {
    return {
      path: getRouteToCoverAbsenceDrawer({ workJourneyConfigId, date }),
      state: {
        ...baseState,
        absentUsers: resolveAbsentUsers(item),
        dateFrom,
        dateTo,
      },
    };
  }

  if (item.state === INSUFFICIENT_ASSIGNED_MEMBERS) {
    return {
      path: getRouteToCoverShortageDrawer({ workJourneyConfigId, date }),
      state: baseState,
    };
  }

  return {
    path: getRouteToCoverShortageDrawer({ workJourneyConfigId, date }),
    state: baseState,
  };
};
